import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Info } from "lucide-react";
import AppShell from "../components/app/AppShell";

const SECTIONS = [
  { key: "tier1", badge: "TIER 1 — HEALTHY DECLINES", color: "#2E7D32", bg: "#E8F5E9", money: "$150 → $300/case",
    title: "Send these all day. Money leads.",
    body: "Clean histories. Conditions are managed and stable. Underwriters say yes more than they say no — your easiest payout.",
    examples: ["Controlled hypertension on one prescription","Mild sleep apnea using CPAP","ADHD treated with medication","Anxiety/depression on stable medication","Acid reflux managed with OTC","BMI under 35 with no co-morbidities","Asthma without ER visits in 2+ years"] },
  { key: "tier2", badge: "TIER 2 — COMPLEX CASES", color: "#E65100", bg: "#FFF3E0", money: "$100 → $200/case",
    title: "Send with context. Workable.",
    body: "Multiple meds, past surgeries now stable, mixed histories. We can place these — just give us the context up front.",
    examples: ["Type 2 diabetes well-managed (A1C under 7)","Past heart procedure, stable for 2+ years","Multiple prescriptions for unrelated conditions","BMI 35-40 with healthy markers","Mental health on multiple meds","Recent surgery (recovered, no follow-ups)"] },
  { key: "tier3", badge: "TIER 3 — HARD TO PLACE", color: "#C62828", bg: "#FFEBEE", money: "$50 → $100/case",
    title: "Still worth sending. Still placed.",
    body: "Pregnancy, severe conditions, recent active treatment. Some carriers will pass — but we have specialty markets that won't.",
    examples: ["Current pregnancy","Type 1 diabetes or uncontrolled Type 2","Cancer history (within 5 years)","COPD or severe asthma","Recent major heart event","Active treatment for chronic illness"] },
];

export default function LeadGuide() {
  const navigate = useNavigate();
  return (
    <AppShell>
      <div className="px-5 lg:px-10 py-8 max-w-3xl">
        <button data-testid="back-btn" onClick={() => navigate(-1)} className="w-10 h-10 grid place-items-center -ml-2 rounded-lg hover:bg-[#F5F5F5]"><ArrowLeft size={20} /></button>
        <span className="eyebrow mt-4 block">LEAD QUALITY GUIDE</span>
        <h1 className="font-display text-3xl sm:text-4xl font-black mt-1">What to send. What to expect.</h1>
        <p className="text-sm mt-2 max-w-xl" style={{ color: "#5A5A5A" }}>Volume scales your rate per case in a rolling calendar month — keep these examples handy when triaging your book.</p>

        <div className="mt-6 space-y-4">
          {SECTIONS.map((s) => (
            <div key={s.key} className="rounded-2xl bg-white p-6 border-2" style={{ borderColor: s.color }}>
              <span className="inline-block px-2.5 py-1 rounded-full text-[10px] font-black tracking-wider" style={{ background: s.bg, color: s.color }}>{s.badge}</span>
              <div className="font-display text-2xl font-black mt-3 tnum">{s.money}</div>
              <h3 className="font-extrabold mt-1">{s.title}</h3>
              <p className="text-sm mt-2 leading-relaxed" style={{ color: "#5A5A5A" }}>{s.body}</p>
              <div className="text-[10px] font-extrabold tracking-wider mt-4 mb-2" style={{ color: "#8A8A8A" }}>EXAMPLES</div>
              {s.examples.map((ex, i) => (
                <div key={i} className="flex items-center gap-2 py-1">
                  <Check size={14} color={s.color} className="shrink-0" />
                  <span className="text-sm">{ex}</span>
                </div>
              ))}
            </div>
          ))}

          <div className="rounded-2xl p-6 text-center" style={{ background: "#E3F0FB" }}>
            <Info size={22} color="#2E78C5" className="mx-auto" />
            <h3 className="font-display text-lg font-black mt-2" style={{ color: "#D62828" }}>The compounding effect</h3>
            <p className="text-sm mt-2 leading-relaxed" style={{ color: "#5A5A5A" }}>
              Pay scales with monthly volume per tier. 5 Tier 1s = $150/case. 11 Tier 1s = $200/case retroactively. More volume across all tiers = bigger checks. When in doubt — send it.
            </p>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
